// The consolidated transition dashboard for one client (ASU PRIORITY SCOPE ADDITION §6): every funded
// technology with its stage, partners versus confirmed program interest, priority, owner, next action and
// pathways; the seven-stage board; stalled technologies; overdue actions; the reverse-ATSP list; and the
// Introduction Pipeline. With clientView the same page renders for a client login, read-only, with only
// client-visible engagements.
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Engagement, Me, Tag, Technology, TransitionDashboard } from '../lib/api';
import { fmtDate, titleCase } from '../lib/format';
import { Empty, Kpi, PageHead, Panel } from '../components/ui';
import { ATSP_LABEL, EngagementForm, EngagementStatusPill, LinkStatusPill, PriorityPill, STAGES, STAGE_LABEL, STAGE_SHORT, StagePill, TechnologyForm, useMembers } from '../components/transition';

export function TransitionPage({ clientId, me, clientView = false }: { clientId: string; me: Me; clientView?: boolean }) {
  const cv = clientView;
  const [d, setD] = useState<TransitionDashboard | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [engaging, setEngaging] = useState(false);
  const [view, setView] = useState<'board' | 'table'>('board');
  const canEdit = !cv && me.role !== 'viewer';
  const members = useMembers(canEdit);

  const load = useCallback(() => {
    api<TransitionDashboard>(`/api/clients/${clientId}/transition`).then((r) => { setD(r); setErr(null); }).catch((e: Error) => setErr(e.message));
  }, [clientId]);
  useEffect(() => { load(); api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([])); }, [load]);

  const techHref = (t: Technology) => (cv ? `#/technology/${t.id}` : `#/clients/${clientId}/technology/${t.id}`);
  const open = (t: Technology) => { location.hash = techHref(t).slice(1); };

  if (err && !d) return <><PageHead title="Technology transition" /><Empty>{err === 'not_found' ? 'This client does not exist or was removed.' : err}</Empty></>;
  if (!d) return <><PageHead title="Loading…" /><Empty>Loading…</Empty></>;

  const techs = d.technologies;
  const engagedPlus = techs.filter((t) => STAGES.indexOf(t.stage) >= STAGES.indexOf('engagement_initiated')).length;
  const confirmed = techs.filter((t) => t.programs.some((p) => p.status === 'interested' || p.status === 'committed')).length;
  const withPartner = techs.filter((t) => t.partners.length > 0).length;

  return (
    <>
      <PageHead
        title={`${cv ? 'Technology transition' : `${d.client.name} · technology transition`}`}
        sub={cv ? 'Where each of your funded technologies stands on the path to a military program of record' : 'Funded projects, military applications, programs of record, engagement and pathways, through seven stages'}
        actions={
          <>
            <span className="filter-bar" style={{ marginBottom: 0 }}>
              <button className={`filter-btn ${view === 'board' ? 'active' : ''}`} onClick={() => setView('board')}>Board</button>
              <button className={`filter-btn ${view === 'table' ? 'active' : ''}`} onClick={() => setView('table')}>Table</button>
            </span>
            {canEdit && <button className="ghost-btn" onClick={() => setEngaging(true)}>Record engagement</button>}
            {canEdit && <button className="primary-btn" onClick={() => setAdding(true)}>Add technology</button>}
            <a className="ghost-btn" href={cv ? '#/dashboard' : `#/clients/${clientId}`}>← {cv ? 'Your dashboard' : d.client.name}</a>
          </>
        }
      />
      {err && <div className="error-text">{err}</div>}
      <div className="kpi-row">
        <Kpi label="Technologies" value={techs.length} hint={`${techs.filter((t) => t.stage === 'transitioned').length} transitioned`} />
        <Kpi label="In engagement or beyond" value={engagedPlus} hint="stage 4 of 7 or later" />
        <Kpi label="Program interest confirmed" value={confirmed} hint={`${withPartner} with a transition partner`} />
        <Kpi label="Stalled" value={d.stalled.length} hint={`no stage change in ${d.stall_days}+ days`} />
        <Kpi label="Overdue actions" value={d.overdue_technologies.length + d.overdue_engagements.length} />
        <Kpi label="Reverse ATSP" value={d.atsp.length} hint="candidates and beyond" />
      </div>

      {techs.length === 0 ? (
        <Panel title="No technologies yet">
          <Empty>{cv ? 'NTN has not loaded your funded projects yet.' : 'Add the client\'s funded projects and technologies. Each one starts at "Technology identified" and moves through the seven stages as military applications, programs and pathways are established.'}</Empty>
        </Panel>
      ) : view === 'board' ? (
        <div className="panel" style={{ marginBottom: 18 }}>
          <div className="panel-body">
            <div className="board">
              {STAGES.map((s) => (
                <div className="board-col" key={s}>
                  <h4 title={STAGE_LABEL[s]}><span>{STAGE_SHORT[s]}</span><span className="count">{d.by_stage[s] ?? 0}</span></h4>
                  {techs.filter((t) => t.stage === s).map((t) => (
                    <div className={`tech-card ${d.stalled.some((x) => x.id === t.id) ? 'stalled' : ''}`} key={t.id} onClick={() => open(t)} title={STAGE_LABEL[s]}>
                      <strong>{t.name}</strong>
                      <div className="meta">
                        <PriorityPill priority={t.priority} />
                        {t.trl && <span>TRL {t.trl}</span>}
                        <span>{t.days_in_stage}d in stage</span>
                        {t.owner_name && <span>· {t.owner_name}</span>}
                      </div>
                      {(t.partners.length > 0 || t.programs.length > 0) && (
                        <div className="meta">{t.partners.length > 0 && <span>Partners: {t.partners.join(', ')}</span>}{t.programs.slice(0, 2).map((p) => <LinkStatusPill key={p.id} status={p.status} />)}</div>
                      )}
                      {t.next_action && <div className={`meta ${t.action_overdue ? 'neg' : ''}`}>→ {t.next_action}{t.next_action_due ? ` (${fmtDate(t.next_action_due)})` : ''}</div>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="panel" style={{ marginBottom: 18 }}>
          <div className="panel-body table-wrap">
            <table className="data-table">
              <thead><tr><th>Technology</th><th>Stage</th><th>Priority</th><th>Transition partners</th><th>Programs and offices</th><th>Pathway</th><th>Owner</th><th>Next action</th><th className="num">Days in stage</th></tr></thead>
              <tbody>
                {techs.map((t) => (
                  <tr key={t.id} className="clickable" onClick={() => open(t)}>
                    <td><strong>{t.name}</strong>{t.trl && <div className="muted sm">TRL {t.trl}</div>}</td>
                    <td><StagePill stage={t.stage} short /></td>
                    <td><PriorityPill priority={t.priority} /></td>
                    <td className="muted">{t.partners.length ? t.partners.join(', ') : '—'}</td>
                    <td>{t.programs.length ? t.programs.map((p) => <div key={p.id}>{p.label} <LinkStatusPill status={p.status} /></div>) : <span className="muted">—</span>}</td>
                    <td className="muted">{[...t.pathways, ...(t.atsp_status !== 'not_applicable' ? [ATSP_LABEL[t.atsp_status]] : [])].join(', ') || '—'}</td>
                    <td className="muted">{t.owner_name ?? '—'}</td>
                    <td>{t.next_action ? <>{t.next_action}{t.next_action_due && <div className={`sm ${t.action_overdue ? 'neg' : 'muted'}`}>{t.action_overdue ? 'overdue ' : 'due '}{fmtDate(t.next_action_due)}</div>}</> : <span className="muted">—</span>}</td>
                    <td className={`num ${d.stalled.some((x) => x.id === t.id) ? 'neg' : ''}`}>{t.days_in_stage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-2">
        <Panel title={`Stalled (${d.stalled.length})`} action={<span className="muted sm">no stage change in {d.stall_days}+ days</span>}>
          {d.stalled.length === 0 ? <div className="muted sm">Nothing stalled.</div> : d.stalled.map((t) => (
            <div className="version" key={t.id}><time>{t.days_in_stage}d</time><span style={{ flex: 1 }}><a className="link" href={techHref(t)}>{t.name}</a><div className="muted sm">{STAGE_LABEL[t.stage]}{t.next_action ? ` · next: ${t.next_action}` : ' · no next action set'}</div></span><StagePill stage={t.stage} short /></div>
          ))}
        </Panel>
        <Panel title={`Overdue actions (${d.overdue_technologies.length + d.overdue_engagements.length})`}>
          {d.overdue_technologies.length + d.overdue_engagements.length === 0 ? <div className="muted sm">No overdue next actions.</div> : (
            <>
              {d.overdue_technologies.map((t) => <div className="version" key={t.id}><time className="neg">{fmtDate(t.next_action_due)}</time><span style={{ flex: 1 }}>{t.next_action}<div className="muted sm"><a className="link" href={techHref(t)}>{t.name}</a>{t.owner_name ? ` · ${t.owner_name}` : ''}</div></span></div>)}
              {d.overdue_engagements.map((e) => <div className="version" key={e.id}><time className="neg">{fmtDate(e.next_action_due)}</time><span style={{ flex: 1 }}>{e.next_action ?? e.title}<div className="muted sm">{e.title}{e.technology_name ? ` · ${e.technology_name}` : ''}{e.owner_name ? ` · ${e.owner_name}` : ''}</div></span><EngagementStatusPill status={e.status} /></div>)}
            </>
          )}
        </Panel>
      </div>

      <div className="grid grid-2">
        <Panel title={`Reverse ATSP (${d.atsp.length})`} action={<span className="muted sm">DMEA Advanced Technology Support Program as the transition vehicle</span>}>
          {d.atsp.length === 0 ? <div className="muted sm">{cv ? 'No technologies on the ATSP pathway yet.' : 'Mark a technology as an ATSP candidate to track outreach to ATSP and its prime program offices here.'}</div> : d.atsp.map((t) => (
            <div className="version" key={t.id}><time><span className="pill info">{ATSP_LABEL[t.atsp_status]}</span></time><span style={{ flex: 1 }}><a className="link" href={techHref(t)}>{t.name}</a>{t.atsp_note && <div className="muted sm">{t.atsp_note}</div>}{t.funding_note && <div className="muted sm">Funding: {t.funding_note}</div>}</span></div>
          ))}
        </Panel>
        <Panel title={cv ? `Engagements NTN has shared (${d.engagements.length})` : `Introduction Pipeline (${d.engagements.length})`} action={canEdit ? <button className="ghost-btn" onClick={() => setEngaging(true)}>Record engagement</button> : undefined}>
          {d.engagements.length === 0 ? <div className="muted sm">{cv ? 'Nothing shared yet.' : 'No engagements recorded. Introductions, outreach, meetings and follow-ups go here, each with an owner, a next action and a due date.'}</div> : d.engagements.slice(0, 12).map((e) => <EngagementLine key={e.id} e={e} techHref={e.technology_id ? (cv ? `#/technology/${e.technology_id}` : `#/clients/${clientId}/technology/${e.technology_id}`) : null} />)}
        </Panel>
      </div>

      {adding && <TechnologyForm clientId={clientId} tags={tags} members={members} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {engaging && <EngagementForm clientId={clientId} technologies={techs} members={members} onClose={() => setEngaging(false)} onSaved={() => { setEngaging(false); load(); }} />}
    </>
  );
}

export function EngagementLine({ e, techHref, onEdit }: { e: Engagement; techHref: string | null; onEdit?: () => void }) {
  return (
    <div className="version" style={{ alignItems: 'flex-start' }}>
      <time>{e.occurred_at ? fmtDate(e.occurred_at) : e.next_action_due ? `due ${fmtDate(e.next_action_due)}` : '—'}</time>
      <span style={{ flex: 1 }}>
        <strong>{e.title}</strong> <span className="muted sm">{titleCase(e.kind)}{e.organization_name ? ` · ${e.organization_name}` : ''}{e.counterpart ? ` · ${e.counterpart}` : ''}</span>
        {e.summary && <div className="muted sm">{e.summary}</div>}
        {e.outcome && <div className="sm">Outcome: {e.outcome}</div>}
        <div className="muted sm">
          {e.technology_name && techHref ? <><a className="link" href={techHref}>{e.technology_name}</a> · </> : ''}
          {e.owner_name ? `${e.owner_name} · ` : ''}
          {e.next_action ? <span className={e.action_overdue ? 'neg' : ''}>next: {e.next_action}{e.next_action_due ? ` (${fmtDate(e.next_action_due)})` : ''}</span> : 'no next action'}
          {e.visibility === 'client' ? ' · visible to client' : ''}
        </div>
      </span>
      <EngagementStatusPill status={e.status} />
      {onEdit && <button className="ghost-btn" onClick={onEdit}>Edit</button>}
    </div>
  );
}
